import type { SxProps } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import type { Formatter } from 'react-timeago';
import ReactTimeAgo from 'react-timeago';
import { makeIntlFormatter } from 'react-timeago/defaultFormatter';
import type { StreamlineFontSizeType } from '../../streamlineIcon/constants';
import { TimeClockCircleAlternate } from '../../streamlineRegularIcon/TimeClockCircleAlternate';
import { Space } from '../../typography';
import { mapTextSize, type Size } from '../../utils/mapTextSize';
import { useLocalizedDateTime } from '../hooks/useLocalizedDateTime';

const MS_PER_24_HOURS = 24 * 60 * 60 * 1000;

// Show "just now" for times less than 1 minute ago, otherwise use the default formatter
const createJustNowFormatter = (
  lang: string | undefined,
  format: TimeAgoProps['format'],
  justNowLabel: string,
): Formatter => {
  const intlFormatter = makeIntlFormatter({
    locale: lang || 'en',
    localeMatcher: 'best fit',
    style: format,
  });
  return (value, unit, suffix, epochMilliseconds, nextFormatter, now) =>
    unit === 'second'
      ? justNowLabel
      : intlFormatter(
          value,
          unit,
          suffix,
          epochMilliseconds,
          nextFormatter,
          now,
        );
};

export interface TimeAgoProps {
  date: string | Date;
  hideIcon?: boolean;
  /** Label shown for timestamps within the last few seconds. Default: "Just now" */
  justNowLabel?: string; // Pass translated string from client, e.g. t('common:time.justNow')
  lang?: string;
  sx?: SxProps;
  size?: Size;
  format?: 'short' | 'long' | 'narrow';
  iconSize?: StreamlineFontSizeType;
}

const DEFAULT_JUST_NOW_LABEL = 'Just now';

export const TimeAgo = ({
  date,
  lang,
  hideIcon,
  justNowLabel = DEFAULT_JUST_NOW_LABEL,
  sx,
  size,
  format,
}: TimeAgoProps) => {
  const dateLocalizedString = useLocalizedDateTime(date);
  const isOlderThan24Hours =
    Date.now() - new Date(date).getTime() > MS_PER_24_HOURS;
  const formatter = useMemo(
    () => createJustNowFormatter(lang, format, justNowLabel),
    [lang, format, justNowLabel],
  );

  return (
    <Tooltip
      describeChild
      title={dateLocalizedString}
      placement="right"
      disableHoverListener={isOlderThan24Hours}
      disableFocusListener={isOlderThan24Hours}
      disableTouchListener={isOlderThan24Hours}
    >
      <Typography
        color="textSecondary"
        component="span"
        variant={mapTextSize(size)}
        noWrap
        sx={{ display: 'inline-flex', alignItems: 'center', ...sx }}
      >
        {!hideIcon && (
          <>
            <TimeClockCircleAlternate />
            <Space />
          </>
        )}
        {isOlderThan24Hours ? (
          dateLocalizedString
        ) : (
          <ReactTimeAgo date={date} component="span" formatter={formatter} />
        )}
      </Typography>
    </Tooltip>
  );
};
